import type { CSSProperties } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../../theme';

export interface AdminAssessmentStatsChartProps {
  passedPercent: number;
  failedPercent: number;
  pendingPercent: number;
  labelPassed: string;
  labelFailed: string;
  labelPending: string;
  loadingLabel: string;
  errorLabel: string;
  loading?: boolean;
  error?: boolean;
}

export function AdminAssessmentStatsChart({
  passedPercent,
  failedPercent,
  pendingPercent,
  labelPassed,
  labelFailed,
  labelPending,
  loadingLabel,
  errorLabel,
  loading,
  error,
}: AdminAssessmentStatsChartProps) {
  const theme = useTheme();
  const c = theme.palette.adminDashboard.charts;
  /** Pizza completa (sem buraco); legenda com percentuais por baixo */
  const pieHeight = Math.max(c.chartHeightPx, 180);

  const pieData = [
    { name: labelPassed, value: Math.max(0, passedPercent), key: 'passed' },
    { name: labelFailed, value: Math.max(0, failedPercent), key: 'failed' },
    { name: labelPending, value: Math.max(0, pendingPercent), key: 'pending' },
  ];

  const colors: Record<string, string> = {
    passed: c.slicePassed,
    failed: c.sliceFailed,
    pending: c.slicePending,
  };

  const bodyStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing.md,
  };

  const chartWrap: CSSProperties = {
    width: '100%',
    height: pieHeight,
    minHeight: pieHeight,
  };

  if (loading) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {loadingLabel}
      </p>
    );
  }

  if (error) {
    return (
      <p
        style={{
          margin: 0,
          color: theme.palette.error.main,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {errorLabel}
      </p>
    );
  }

  const statRow: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  };

  const statBlock: CSSProperties = {
    flex: '1 1 80px',
    minWidth: 0,
    textAlign: 'center',
  };

  const sliceStroke = c.cardBackground;

  return (
    <div style={bodyStyle}>
      <div style={chartWrap}>
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius="82%"
              paddingAngle={1}
              stroke={sliceStroke}
              strokeWidth={2}
            >
              {pieData.map(entry => (
                <Cell
                  key={entry.key}
                  fill={colors[entry.key]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => `${value}%`}
              contentStyle={{
                borderRadius: theme.shape.borderRadius.sm2,
                border: `${theme.shape.borderWidth.thin} solid ${c.cardBorder}`,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={statRow}>
        <div style={statBlock}>
          <div
            style={{
              fontSize: theme.typography.fontSize.chartStatPercent,
              fontWeight: theme.typography.fontWeight.bold,
              color: c.slicePassed,
              lineHeight: theme.typography.lineHeight.tight,
            }}
          >
            {passedPercent}%
          </div>
          <div
            style={{
              fontSize: theme.typography.fontSize.xs,
              color: c.statLabelMuted,
              marginTop: theme.spacing.xs,
            }}
          >
            {labelPassed}
          </div>
        </div>
        <div style={statBlock}>
          <div
            style={{
              fontSize: theme.typography.fontSize.chartStatPercent,
              fontWeight: theme.typography.fontWeight.bold,
              color: c.sliceFailed,
              lineHeight: theme.typography.lineHeight.tight,
            }}
          >
            {failedPercent}%
          </div>
          <div
            style={{
              fontSize: theme.typography.fontSize.xs,
              color: c.statLabelMuted,
              marginTop: theme.spacing.xs,
            }}
          >
            {labelFailed}
          </div>
        </div>
        <div style={statBlock}>
          <div
            style={{
              fontSize: theme.typography.fontSize.chartStatPercent,
              fontWeight: theme.typography.fontWeight.bold,
              color: c.slicePending,
              lineHeight: theme.typography.lineHeight.tight,
            }}
          >
            {pendingPercent}%
          </div>
          <div
            style={{
              fontSize: theme.typography.fontSize.xs,
              color: c.statLabelMuted,
              marginTop: theme.spacing.xs,
            }}
          >
            {labelPending}
          </div>
        </div>
      </div>
    </div>
  );
}
